/**
 * RealPanel deep-link routing: `?act=<action>[&param=...]`
 *
 * Every WHM / cPanel screen is addressable, bookmarkable, and works with
 * refresh + browser back/forward. Permissions are still enforced in App.tsx
 * when applying a route (unauthorized → home).
 */
import type { NavKey } from "../types";
import { WHM_TOOLS } from "../whmTools";
import { CPANEL_TOOLS } from "../cpanelTools";

export type AdvancedTab =
  | "overview"
  | "cluster"
  | "limits"
  | "integrations"
  | "licenses"
  | "appearance";

function advancedTabFromNav(nav: string): AdvancedTab {
  if (nav === "servers") return "cluster";
  if (nav === "isolation") return "limits";
  if (nav === "webhooks") return "integrations";
  if (nav === "licenses") return "licenses";
  if (nav === "polish") return "appearance";
  return "overview";
}

export type ServerConfigTab =
  | "status"
  | "basic"
  | "tweaks"
  | "php"
  | "metrics"
  | "panel";

export type FtpTab = "manage" | "add" | "connections";

/** Full navigation state encoded into the URL */
export type RouteState = {
  nav: NavKey;
  /** Tool id (whm/cpanel) when known */
  toolId: string | null;
  /** Canonical act string for the URL */
  act: string;
  createAccount?: boolean;
  serverConfigTab?: ServerConfigTab;
  advancedTab?: AdvancedTab;
  ftpTab?: FtpTab;
  sitesMode?: "list" | "add";
};

/** Friendly aliases → tool id (user-facing act names) */
const ACT_ALIASES: Record<string, string> = {
  // Home
  home: "home",
  overview: "home",
  dashboard: "home",
  // Accounts
  account_list: "list-accounts",
  accounts_list: "list-accounts",
  list_accounts: "list-accounts",
  create_account: "create-account",
  new_account: "create-account",
  modify_account: "modify-account",
  // Domains
  domains_list: "domain-list",
  domain_list: "domain-list",
  list_domains: "domain-list",
  domains: "domain-list",
  add_domain: "add-domain",
  all_sites: "all-sites",
  // PHP / software
  multiphp: "multiphp-manager",
  multiphp_manager: "multiphp-manager",
  php_fpm: "php-fpm-settings",
  php_fpm_settings: "php-fpm-settings",
  php_extensions: "php-extensions",
  php_modules: "php-extensions",
  // Databases
  mysql_databases: "mysql",
  mysql: "mysql",
  databases: "mysql",
  phpmyadmin: "phpmyadmin",
  pma: "phpmyadmin",
  db_users: "db-users",
  // SSL
  automatic_ssl: "auto-ssl",
  auto_ssl: "auto-ssl",
  ssl: "auto-ssl",
  panel_ssl: "panel-domain-ssl",
  panel_domain_ssl: "panel-domain-ssl",
  // Apps
  website_apps: "enduser-apps",
  enduser_apps: "enduser-apps",
  install_apps: "install-apps",
  installed_apps: "installed-apps",
  softaculous: "softaculous",
  // FTP
  ftp_accounts: "manage-ftp",
  manage_ftp: "manage-ftp",
  add_ftp: "add-ftp",
  ftp_connections: "ftp-connections",
  // Files / email / etc
  file_manager: "file-manager",
  files: "file-manager",
  email: "email-accounts",
  email_accounts: "email-accounts",
  // Plans / packages
  hosting_plans: "hosting-plans",
  plans: "hosting-plans",
  owner_packages: "owner-packages",
  packages: "owner-packages",
  // Server
  panel_config: "panel-config",
  server_status: "service-status",
  service_status: "service-status",
  // Security
  security: "security-advisor",
  firewall: "firewall",
  // Backup / transfer
  backups: "backup-config",
  backup: "backup-config",
  import: "import-webuzo",
  migrations: "transfer",
  // Misc
  cron: "cron",
  logs: "errors",
  dns: "dns-zone",
  branding: "branding",
  appearance: "appearance",
  licenses: "licenses",
  audit: "audit",
  api_tokens: "api-tokens",
  tokens: "api-tokens",
  resellers: "reseller-center",
  reseller_center: "reseller-center"
};

/** Nav-only defaults when no tool id (sidebar section click) */
const NAV_DEFAULT_ACT: Record<NavKey, string> = {
  overview: "home",
  accounts: "list_accounts",
  resellers: "reseller_center",
  servers: "cluster",
  migrations: "transfer",
  import: "import_webuzo",
  webhooks: "integrations",
  licenses: "licenses",
  isolation: "resource_limits",
  polish: "appearance",
  advanced: "advanced_overview",
  branding: "branding",
  sites: "domains_list",
  files: "file_manager",
  ftp: "ftp_accounts",
  databases: "mysql_databases",
  dns: "dns_zone",
  email: "email",
  cron: "cron",
  logs: "logs",
  metrics: "server_status",
  backups: "backups",
  apps: "website_apps",
  php: "php_extensions",
  packages: "owner_packages",
  plans: "hosting_plans",
  audit: "audit",
  tokens: "api_tokens",
  security: "security",
  ssl: "automatic_ssl"
};

/** Params we keep when rewriting the URL (besides act) */
const PRESERVE_PARAMS = [
  "reseller",
  "userId",
  "user",
  "domain",
  "site",
  "tab",
  "id",
  "q",
  "path",
  "view"
] as const;

function toolIdToAct(toolId: string): string {
  return toolId.replace(/-/g, "_");
}

function actToToolId(act: string): string {
  return act.replace(/_/g, "-");
}

/** Build route resolution table from tool catalogs */
function buildToolRouteIndex(): Map<
  string,
  {
    toolId: string;
    nav: NavKey;
    hint?: string;
  }
> {
  const map = new Map<
    string,
    { toolId: string; nav: NavKey; hint?: string }
  >();
  const add = (id: string, nav: NavKey, hint?: string) => {
    const act = toolIdToAct(id);
    map.set(act, { toolId: id, nav, hint });
    map.set(id, { toolId: id, nav, hint }); // also allow hyphen form
  };
  for (const t of WHM_TOOLS) add(t.id, t.nav, t.hint);
  for (const t of CPANEL_TOOLS) add(t.id, t.nav, t.hint);
  // Synthetic home
  map.set("home", { toolId: "home", nav: "overview" });
  return map;
}

const TOOL_INDEX = buildToolRouteIndex();

function routeExtrasFromTool(
  toolId: string,
  hint?: string
): Partial<RouteState> {
  const extra: Partial<RouteState> = { toolId };
  if (toolId === "create-account" || hint === "create") {
    extra.createAccount = true;
  }
  if (toolId === "add-domain" || toolId === "addon-domain") {
    extra.sitesMode = "add";
  }
  if (toolId === "domain-list" || toolId === "domains" || toolId === "all-sites") {
    extra.sitesMode = "list";
  }
  // FTP
  if (toolId === "manage-ftp") extra.ftpTab = "manage";
  if (toolId === "add-ftp") extra.ftpTab = "add";
  if (toolId === "ftp-connections") extra.ftpTab = "connections";
  // Server config tabs via tool
  if (
    toolId === "panel-config" ||
    toolId === "service-status" ||
    toolId === "server-info" ||
    toolId === "basic-config" ||
    toolId === "tweaks"
  ) {
    const tabMap: Record<string, ServerConfigTab> = {
      "panel-config": "panel",
      "service-status": "status",
      "server-info": "status",
      "basic-config": "basic",
      tweaks: "tweaks"
    };
    extra.serverConfigTab = tabMap[toolId] || "panel";
  }
  if (
    toolId === "php-extensions" ||
    toolId === "multiphp-manager" ||
    toolId === "php-fpm-settings" ||
    toolId === "edit-php-ini"
  ) {
    // Most PHP tools live under apps services or dedicated pages via apps nav
    // multiphp/fpm open via apps or metrics — keep nav from tool
  }
  // Advanced
  if (toolId === "advanced-overview") extra.advancedTab = "overview";
  if (toolId === "cluster") extra.advancedTab = "cluster";
  if (toolId === "resource-limits") extra.advancedTab = "limits";
  if (toolId === "integrations") extra.advancedTab = "integrations";
  if (toolId === "licenses") extra.advancedTab = "licenses";
  if (toolId === "appearance") extra.advancedTab = "appearance";
  // Server config php tab
  if (hint === "php" || toolId === "php-config") {
    extra.serverConfigTab = "php";
  }
  if (hint && ["status", "basic", "tweaks", "php", "metrics", "panel"].includes(hint)) {
    extra.serverConfigTab = hint as ServerConfigTab;
  }
  return extra;
}

/**
 * Resolve any act string (alias, tool id, underscore form) to RouteState.
 */
export function resolveAct(actRaw: string | null | undefined): RouteState {
  const raw = String(actRaw || "home")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "");
  if (!raw || raw === "home" || raw === "overview" || raw === "dashboard") {
    return { nav: "overview", toolId: null, act: "home" };
  }

  // Alias first
  const aliased = ACT_ALIASES[raw] || ACT_ALIASES[raw.replace(/-/g, "_")];
  const key = aliased || raw;
  const actKey = key.replace(/-/g, "_");

  const hit =
    TOOL_INDEX.get(actKey) ||
    TOOL_INDEX.get(key) ||
    TOOL_INDEX.get(actToToolId(actKey));

  if (hit) {
    const extras = routeExtrasFromTool(hit.toolId, hit.hint);
    return {
      nav: hit.nav,
      toolId: hit.toolId === "home" ? null : hit.toolId,
      act: toolIdToAct(hit.toolId === "home" ? "home" : hit.toolId),
      ...extras
    };
  }

  // Direct nav key fallback (e.g. act=sites, act=email)
  const navKeys: NavKey[] = [
    "overview",
    "accounts",
    "resellers",
    "servers",
    "migrations",
    "import",
    "webhooks",
    "licenses",
    "isolation",
    "polish",
    "advanced",
    "branding",
    "sites",
    "files",
    "ftp",
    "databases",
    "dns",
    "email",
    "cron",
    "logs",
    "metrics",
    "backups",
    "apps",
    "php",
    "packages",
    "plans",
    "audit",
    "tokens",
    "security",
    "ssl"
  ];
  const asNav = actKey.replace(/-/g, "_");
  // map domains → sites etc
  const navAlias: Record<string, NavKey> = {
    domains: "sites",
    domain: "sites",
    mysql_databases: "databases",
    database: "databases",
    website_apps: "apps",
    applications: "apps",
    ftp_accounts: "ftp",
    automatic_ssl: "ssl",
    account_list: "accounts",
    hosting_plans: "plans",
    owner_packages: "packages",
    server_status: "metrics",
    file_manager: "files"
  };
  const nav =
    navAlias[asNav] ||
    (navKeys.includes(asNav as NavKey) ? (asNav as NavKey) : null);
  if (nav) {
    return {
      nav,
      toolId: null,
      act: NAV_DEFAULT_ACT[nav] || asNav,
      ...(nav === "accounts" && asNav === "create_account"
        ? { createAccount: true }
        : {})
    };
  }

  // Unknown → home
  return { nav: "overview", toolId: null, act: "home" };
}

/** Build canonical act for a navigation action */
export function actFromNav(
  nav: NavKey,
  opts?: {
    toolId?: string | null;
    createAccount?: boolean;
    serverConfigTab?: ServerConfigTab;
    advancedTab?: AdvancedTab;
    ftpTab?: FtpTab;
  }
): string {
  if (opts?.toolId) return toolIdToAct(opts.toolId);
  if (opts?.createAccount) return "create_account";
  if (nav === "ftp" && opts?.ftpTab) {
    if (opts.ftpTab === "add") return "add_ftp";
    if (opts.ftpTab === "connections") return "ftp_connections";
    return "ftp_accounts";
  }
  if (nav === "sites" && opts?.toolId === "add-domain") return "add_domain";
  if (
    (nav === "metrics" || nav === "php") &&
    opts?.serverConfigTab &&
    opts.serverConfigTab !== "status"
  ) {
    if (opts.serverConfigTab === "panel") return "panel_config";
    if (opts.serverConfigTab === "basic") return "basic_config";
    if (opts.serverConfigTab === "tweaks") return "tweaks";
    if (opts.serverConfigTab === "php") return "php_extensions";
  }
  if (nav === "advanced" && opts?.advancedTab) {
    const m: Record<AdvancedTab, string> = {
      overview: "advanced_overview",
      cluster: "cluster",
      limits: "resource_limits",
      integrations: "integrations",
      licenses: "licenses",
      appearance: "appearance"
    };
    return m[opts.advancedTab] || "advanced_overview";
  }
  return NAV_DEFAULT_ACT[nav] || toolIdToAct(nav);
}

export function routeStateFromNav(
  nav: NavKey,
  opts?: {
    toolId?: string | null;
    createAccount?: boolean;
    serverConfigTab?: ServerConfigTab;
    advancedTab?: AdvancedTab;
    ftpTab?: FtpTab;
  }
): RouteState {
  const act = actFromNav(nav, opts);
  const resolved = resolveAct(act);
  // Prefer explicit opts over resolve defaults
  return {
    ...resolved,
    nav,
    toolId: opts?.toolId !== undefined ? opts.toolId || null : resolved.toolId,
    act,
    createAccount: opts?.createAccount ?? resolved.createAccount,
    serverConfigTab: opts?.serverConfigTab ?? resolved.serverConfigTab,
    advancedTab:
      opts?.advancedTab ??
      resolved.advancedTab ??
      (nav === "advanced" ||
      nav === "servers" ||
      nav === "isolation" ||
      nav === "webhooks" ||
      nav === "licenses"
        ? advancedTabFromNav(nav)
        : undefined),
    ftpTab: opts?.ftpTab ?? resolved.ftpTab
  };
}

/** Read current browser URL into RouteState */
export function parseLocationRoute(): RouteState {
  try {
    const sp = new URLSearchParams(window.location.search);
    const act = sp.get("act") || sp.get("page") || sp.get("module");
    return resolveAct(act);
  } catch {
    return { nav: "overview", toolId: null, act: "home" };
  }
}

/** Build `?act=…` href for sidebar / tool links (keeps reseller etc.). */
export function hrefForAct(
  act: string,
  extra?: Record<string, string | undefined | null>
): string {
  try {
    const next = new URLSearchParams();
    const cur = new URLSearchParams(window.location.search);
    for (const key of PRESERVE_PARAMS) {
      const v = cur.get(key);
      if (v) next.set(key, v);
    }
    const a = String(act || "home")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "_");
    next.set("act", a || "home");
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        if (v != null && String(v) !== "") next.set(k, String(v));
      }
    }
    const qs = next.toString();
    return `${window.location.pathname}${qs ? `?${qs}` : ""}`;
  } catch {
    return `?act=${encodeURIComponent(act || "home")}`;
  }
}

/** Href from nav + optional tool (used by Shell). */
export function hrefForNav(
  nav: NavKey,
  opts?: {
    toolId?: string | null;
    createAccount?: boolean;
    ftpTab?: FtpTab;
    serverConfigTab?: ServerConfigTab;
    advancedTab?: AdvancedTab;
  }
): string {
  return hrefForAct(actFromNav(nav, opts));
}

/** Write route into the address bar (push or replace). */
export function writeLocationRoute(
  state: RouteState,
  mode: "push" | "replace" = "push"
): void {
  try {
    const url = new URL(window.location.href);
    const next = new URLSearchParams();

    // Preserve selected context params from current URL
    const cur = new URLSearchParams(window.location.search);
    for (const key of PRESERVE_PARAMS) {
      const v = cur.get(key);
      if (v) next.set(key, v);
    }

    const act = state.act || "home";
    if (act === "home") {
      // Clean home URL: optional act=home or omit — keep act for consistency
      next.set("act", "home");
    } else {
      next.set("act", act);
    }

    // Sync optional structural params
    if (state.ftpTab && state.ftpTab !== "manage") {
      next.set("tab", state.ftpTab);
    }
    if (state.serverConfigTab && state.serverConfigTab !== "status") {
      next.set("tab", state.serverConfigTab);
    }
    if (state.advancedTab && state.advancedTab !== "overview") {
      next.set("tab", state.advancedTab);
    }
    if (state.sitesMode === "add") {
      next.set("view", "add");
    }

    const qs = next.toString();
    const path = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash || ""}`;
    const curFull = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (path === curFull) return;

    if (mode === "replace") {
      window.history.replaceState({ act: state.act }, "", path);
    } else {
      window.history.pushState({ act: state.act }, "", path);
    }
  } catch {
    /* ignore */
  }
}

/** Staff/root-only tool ids (resellers/users must not open via URL) */
export const ROOT_ONLY_TOOL_IDS = new Set(
  WHM_TOOLS.filter((t) => t.rootOnly).map((t) => t.id)
);

/** Nav keys restricted to staff (not reseller/end-user) when not impersonating */
export const STAFF_ONLY_NAV: Set<NavKey> = new Set([
  "servers",
  "webhooks",
  "licenses",
  "isolation",
  "audit",
  "tokens",
  "import",
  "migrations",
  "packages",
  "resellers"
]);

/** Super Admin only (even normal Admin cannot open via URL) */
export const SUPERADMIN_ONLY_NAV: Set<NavKey> = new Set(["packages"]);

export function isRouteAllowed(
  state: RouteState,
  opts: {
    isStaff: boolean;
    isSuperAdmin?: boolean;
    isReseller: boolean;
    isEndUser: boolean;
    impersonating?: boolean;
    userFeatures?: Record<string, boolean> | null;
  }
): boolean {
  const { nav, toolId } = state;
  const isSuperAdmin = Boolean(opts.isSuperAdmin);

  // End-user / impersonation: only block when feature is explicitly false
  if (opts.isEndUser || opts.impersonating) {
    const featureKey =
      nav === "ftp"
        ? "files"
        : nav === "ssl"
          ? "sites"
          : nav === "cron"
            ? "cron"
            : nav;
    if (
      opts.userFeatures &&
      nav !== "overview" &&
      opts.userFeatures[featureKey] === false
    ) {
      return false;
    }
    // End-user allowed nav surface (hosting customer tools)
    if (opts.isEndUser && !opts.impersonating) {
      const allowed: NavKey[] = [
        "overview",
        "sites",
        "files",
        "ftp",
        "databases",
        "dns",
        "email",
        "cron",
        "logs",
        "metrics",
        "backups",
        "apps",
        "security",
        "ssl",
        "polish"
      ];
      if (!allowed.includes(nav)) return false;
    }
  }

  // Root-only tools (staff only)
  if (toolId && ROOT_ONLY_TOOL_IDS.has(toolId) && !opts.isStaff) {
    // Resellers may open non-rootOnly tools; end-users never open rootOnly
    return false;
  }

  // Super Admin only surfaces (Owner Packages)
  if (SUPERADMIN_ONLY_NAV.has(nav) && !isSuperAdmin) {
    return false;
  }
  if (
    (toolId === "owner-packages" ||
      toolId === "vps-plans" ||
      toolId === "reseller-plans") &&
    !isSuperAdmin
  ) {
    return false;
  }

  // Staff-only nav for resellers (not impersonating)
  if (opts.isReseller && !opts.impersonating && !opts.isStaff) {
    if (STAFF_ONLY_NAV.has(nav)) return false;
  }

  return true;
}
